<script setup lang="ts">
  import { ref } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useGoogleAnalytics } from '@/shared/composables'
  import type { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'
  import type { Locale } from '@/shared/types/common.types'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'

  interface Props {
    isAddButtonShown?: boolean
    quoteId?: number
    cyberState?: boolean
  }

  interface Emits {
    (e: 'add-cyber-cover'): void
    (e: 'close'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isAddButtonShown: false,
    quoteId: undefined,
    cyberState: false,
  })

  const emit = defineEmits<Emits>()

  const gaService = useGoogleAnalytics()
  const route = useRoute()
  const { locale } = useI18n()

  const modalRef = ref<InstanceType<typeof ModalQC> | null>(null)

  const closeModal = () => {
    modalRef.value?.close()
  }

  const onAddBtnClicked = () => {
    emit('add-cyber-cover')
    closeModal()
    if (!props.quoteId) throw new Error('The quote number is undefined.')
    gaService.sendAddCyberToCoverage(
      route.name as InboundFlowRoutes | OutboundFlowRoutes,
      props.quoteId
    )
  }
</script>

<template>
  <ModalQC
    ref="modalRef"
    :locale="locale as Locale"
    position="side"
    hide-controls
    :title="$t('cyber_security_addon.name')"
    @close="emit('close')"
  >
    <template #content>
      <WrapperQC direction="column" gap="var(--spacing-6)" class="cyber-modal-qc">
        <WrapperQC direction="column" gap="var(--spacing-6)" class="cyber-modal-qc__wrapper">
          <WrapperQC class="cyber-modal-qc__card" direction="column">
            <WrapperQC class="cyber-modal-qc__header" direction="column" gap="var(--spacing-2)">
              <h3 class="cyber-modal-content__subtitle heading-6">
                {{ $t('cyber_security_addon.popup.header.1') }}
              </h3>
              <p class="cyber-modal-qc__desc text-small-regular">
                {{ $t('cyber_security_addon.popup.desc') }}
              </p>
            </WrapperQC>

            <WrapperQC class="cyber-modal-qc__card-content" direction="column">
              <WrapperQC v-for="i in 6" :key="i" direction="column" class="cyber-modal-qc__item">
                <CellQC
                  :title="$t(`cyber_security_addon.popup.block.${i}.header`)"
                  title-color="primary"
                  padding-block="spacing-2"
                  class="cyber-modal-qc__item-cell"
                >
                  <template #before-title>
                    <IconQC name="check-circle" color="var(--colorIcnSrfAction)" />
                  </template>
                </CellQC>
                <p class="cyber-modal-qc__item-desc cyber-modal-qc__item-desc-covered text-regular">
                  {{ $t(`cyber_security_addon.popup.block.${i}.desc`) }}
                </p>
              </WrapperQC>
            </WrapperQC>
          </WrapperQC>

          <WrapperQC class="cyber-modal-qc__card" direction="column">
            <WrapperQC class="cyber-modal-qc__header" direction="column" gap="var(--spacing-2)">
              <h3 class="cyber-modal-content__subtitle heading-6">
                {{ $t('cyber_security_addon.popup.header.2') }}
              </h3>
            </WrapperQC>

            <WrapperQC class="cyber-modal-qc__card-content" direction="column">
              <CellQC
                v-for="i in 4"
                :key="i"
                :title="$t(`cyber_security_addon.popup.list.${i}`)"
                class="cyber-modal-qc__item-cell"
                title-color="primary"
                padding-block="spacing-6"
                :with-border="i !== 4"
              >
                <template #before-title>
                  <IconQC name="close-circle" color="var(--colorIcnSrfTertiary)" />
                </template>
              </CellQC>
            </WrapperQC>
          </WrapperQC>
        </WrapperQC>

        <WrapperQC direction="column" gap="var(--spacing-5)">
          <ButtonQC
            v-if="isAddButtonShown && !cyberState"
            size="medium"
            wide
            @click="onAddBtnClicked"
          >
            {{ $t('cyber_security_addon.popup.button.add') }}
          </ButtonQC>
          <ButtonQC size="medium" variant="secondary" wide @click="closeModal">
            {{ $t('qic.close') }}
          </ButtonQC>
        </WrapperQC>
      </WrapperQC>
    </template>
  </ModalQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils' as *;

  .cyber-modal-qc {
    height: 100%;
    justify-content: space-between;
  }

  .cyber-modal-qc__card {
    border-radius: var(--radius-6);
    padding-block: var(--spacing-7) var(--spacing-5);
    background-color: var(--colorBgSrfTertiary);
  }

  .cyber-modal-qc__header {
    padding-bottom: var(--spacing-7);
    padding-inline: var(--spacing-7);
    border-bottom: 1px solid var(--colorBrdrSrfPrimary);
  }

  .cyber-modal-qc__card-content {
    padding-inline: var(--spacing-7);
  }

  .cyber-modal-qc__item-desc {
    color: var(--colorTxtSrfSecondary);
  }

  .cyber-modal-qc__desc {
    color: var(--colorTxtSrfTertiary);
  }

  .cyber-modal-qc__item-desc-covered {
    padding-inline-start: var(--spacing-8);
  }

  .cyber-modal-qc__item {
    padding-block: var(--spacing-5);
    border-bottom: 1px solid var(--colorBrdrSrfPrimary);

    &:last-child {
      border-bottom: none;
    }
  }

  .cyber-modal-qc__item-cell {
    border-color: var(--colorBrdrSrfPrimary);

    :deep(.ds-cell__title) {
      display: flex;
      gap: var(--spacing-3);

      svg {
        flex-shrink: 0;
      }
    }
  }
</style>
